/** @jsx jsx */

import { Fragment } from 'react';
import { Link, Surface, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';

import TadaSVG from './images/tada.svg';

import PendingCongrats from './Dashboard/PendingCongrats';

import type { Status } from 'app/developer-v3/components/LaunchProgress';

const Styles = {
  root: css`
    display: flex;
    gap: 20px;
    padding: 30px;
  `,
  tadaSVG: css`
    flex: 0 0 52px;
  `,
};

function getCongrats(slug: string | null, status: Status) {
  /** The 'pending' status handling has been transitioned to the newer PendingCongrats component. The original 'pending' content in this function is deprecated but retained for reference. */
  const statuses = {
    pending: {
      description:
        'Your integration was successfully submitted! Our team is working to review it.',
      image: <TadaSVG alt="Tada" css={Styles.tadaSVG} />,
    },
    beta: {
      description: (
        <Fragment>
          Your integration is now published on the{' '}
          <Link
            href={
              slug
                ? `${ZAPIER_APP_BASE_URL}/apps/${slug}/integrations`
                : `${ZAPIER_APP_BASE_URL}/apps?filter=beta&sort=created`
            }
            target="_blank"
          >
            App Directory in beta.
          </Link>{' '}
          After 3 months, your integration’s beta tag will be removed and you’ll
          automatically join the Zapier Partner Program.
        </Fragment>
      ),
      image: null,
    },
  };

  return statuses[status.toLowerCase()] || {};
}

type Props = {
  appId: number;
  slug: string | null;
  status: Status;
};

export default function Congrats({ appId, slug, status }: Props) {
  const { image, description } = getCongrats(slug, status);

  if (status === 'pending') {
    return <PendingCongrats appId={appId} />;
  }

  return (
    <Surface backgroundColor="PrimeBlack">
      <div css={Styles.root}>
        {image}
        <div>
          <Text color="PrimeWhite" fontWeight={600} tag="h3" type="Body1">
            Congrats{status === 'beta' ? ' on entering beta!' : '!'}
          </Text>
          <Text
            color="PrimeWhite"
            margin="20px 0px 0px 0px"
            tag="p"
            type="Body1"
          >
            {description}
          </Text>
        </div>
      </div>
    </Surface>
  );
}
